import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Subject, takeUntil } from 'rxjs';

import type { TrackingActivity } from '@core/activities-types';

import { RequisitionsDataProviderService } from '../../data/single-requisition/requisitions-data-provider.service';

@Injectable()
export class ActivityFacade {
  private readonly router = inject(Router);
  private readonly requisitionActivitiesListByAssociation = {
    approvalFlow: ['requisition_approvers_changed', 'requisition_approval_flow_changed'],
    teamMembers: [
      'requisition_recruiter_change',
      'requisition_recruiter_clear',
      'requisition_hiring_manager_change',
      'requisition_hiring_manager_clear',
    ],
    notes: ['requisition_note_add', 'requisition_note_remove'],
    attachments: ['requisition_attachment_upload', 'requisition_attachment_remove'],
    jobs: ['requisition_job_added', 'requisition_assigned', 'requisition_unassigned'],
    locations: [
      'requisition_location_updated',
      'location_assigned_requisition_archived',
      'location_assigned_requisition_retrieved',
      'requisition_work_model_updated',
    ],
  };

  protected readonly requisitionsData = inject(RequisitionsDataProviderService);

  private requisitionId: number;
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const requisitionsData = this.requisitionsData;

    requisitionsData.data$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(({ requisition }) => {
      this.requisitionId = requisition.id;
    });
  }

  public openActivity(activity: TrackingActivity): void {
    if (activity.candidate) {
      this.router.navigate([], {
        queryParams: { candidate: activity.candidate.id },
        queryParamsHandling: 'merge',
      });
      return;
    }

    if (this.requisitionActivitiesListByAssociation.notes.includes(activity.event)) {
      this.router.navigateByUrl(`/requisitions/${this.requisitionId}/notes`);
      return;
    }

    if (this.requisitionActivitiesListByAssociation.attachments.includes(activity.event)) {
      this.router.navigateByUrl(`/requisitions/${this.requisitionId}/files`);
      return;
    }

    if (this.requisitionActivitiesListByAssociation.teamMembers.includes(activity.event)) {
      this.router.navigateByUrl(`/requisitions/${this.requisitionId}/team`);
      return;
    }

    if (this.requisitionActivitiesListByAssociation.approvalFlow.includes(activity.event)) {
      this.router.navigateByUrl(`/requisitions/${this.requisitionId}/flow`);
      return;
    }

    if (this.requisitionActivitiesListByAssociation.locations.includes(activity.event)) {
      this.router.navigateByUrl(`/requisitions/${this.requisitionId}/details`);
      return;
    }

    if (
      activity.offer &&
      this.requisitionActivitiesListByAssociation.jobs.includes(activity.event)
    ) {
      this.router.navigateByUrl(`/requisitions/${this.requisitionId}/jobs`);
      return;
    }

    this.router.navigateByUrl(`/requisitions/${this.requisitionId}/details`);
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
